// Cave84 — Drink Soon page
// Full list of bottles at/near drinking window, grouped by urgency status.
// Mirrors Dashboard DrinkSoon widget but unbounded + richer per-row data.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGroupedInventory, DEFAULT_FILTERS, type GroupedInventoryRow } from '@/hooks/useInventory';
import { useIsMobile } from '@/hooks/useIsMobile';

// ---------------------------------------------------------------------------
// Bronze Nocturne tokens (same as DashboardPage)
// ---------------------------------------------------------------------------
const BN = {
  bg:          '#0E1116',
  bg2:         '#141821',
  bg3:         '#1B202B',
  surf:        '#181D26',
  hair:        '#232936',
  hair2:       '#2F3645',
  ink:         '#ECE6D4',
  ink2:        '#B3AC97',
  ink3:        '#9C9483',
  ink4:        '#908977',
  accent:      '#C49255',
  accentSoft:  'rgba(196,146,85,0.16)',
  accentDeep:  '#8E663A',
  pos:         '#8FB57C',
  neg:         '#C46F50',
  cool:        '#7E9A98',
};

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------
type StatusGroup = 'declining';

const STATUS_ORDER: StatusGroup[] = ['declining'];

const STATUS_META: Record<StatusGroup, { label: string; color: string; border: string; desc: string }> = {
  declining: { label: 'Declining', color: BN.neg, border: '#8E4F38', desc: 'Past peak, drink soon' },
};

function statusGroup(status: string): StatusGroup | null {
  if (status === 'declining') return 'declining';
  return null;
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------
const ALL_FILTERS = { ...DEFAULT_FILTERS, availableAtHome: false, sortBy: 'drinking_window' as const };

// ---------------------------------------------------------------------------
// DrinkSoonPage
// ---------------------------------------------------------------------------
export default function DrinkSoonPage() {
  const isMobile = useIsMobile();
  const { grouped: rows } = useGroupedInventory(ALL_FILTERS);
  const [showAll, setShowAll] = useState<Record<StatusGroup, boolean>>({
    declining: false,
  });

  // Group rows into peak / developing / declining, filter out too_young + over_the_hill
  // Developing here = within 3 years of window open
  const groups = useMemo(() => {
    const map: Record<StatusGroup, GroupedInventoryRow[]> = {
      declining: [],
    };

    for (const r of rows) {
      const sg = statusGroup(r.drinkingStatus);
      if (!sg) continue;
      map[sg].push(r);
    }

    // Sort each group by window end (ascending = most urgent first)
    for (const g of STATUS_ORDER) {
      map[g].sort((a, b) => a.drinkingWindow.to - b.drinkingWindow.to);
    }

    return map;
  }, [rows]);

  const totalBottles = useMemo(
    () => STATUS_ORDER.reduce((s, g) => s + groups[g].reduce((n, r) => n + r.qty, 0), 0),
    [groups]
  );

  return (
    <div style={{ background: BN.bg, minHeight: '100vh', padding: isMobile ? '20px 16px 80px' : '32px 24px 64px' }}>

      {/* ── Page header ── */}
      <div style={{ maxWidth: 920, margin: '0 auto 36px' }}>
        <h1 style={{
          fontFamily: "'Fraunces', serif",
          fontWeight: 300,
          fontSize: 32,
          letterSpacing: '-0.02em',
          color: BN.ink,
          margin: 0,
        }}>
          Drink Soon
        </h1>
        <p style={{ fontSize: 13, color: BN.ink3, marginTop: 6 }}>
          {totalBottles} bottle{totalBottles !== 1 ? 's' : ''} at or approaching their drinking window
        </p>
      </div>

      {/* ── Status groups ── */}
      <div style={{ maxWidth: 920, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 32 }}>
        {STATUS_ORDER.map(sg => {
          const meta = STATUS_META[sg];
          const allRows = groups[sg];
          const LIMIT = 20;
          const visible = showAll[sg] ? allRows : allRows.slice(0, LIMIT);
          const bottleCount = allRows.reduce((n, r) => n + r.qty, 0);

          if (allRows.length === 0) return null;

          return (
            <section key={sg}>
              {/* Group header */}
              <div style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 12,
                marginBottom: 12,
                paddingBottom: 10,
                borderBottom: `1px solid ${BN.hair}`,
              }}>
                <span style={{
                  fontFamily: "'Fraunces', serif",
                  fontStyle: 'italic',
                  fontSize: 20,
                  fontWeight: 300,
                  color: meta.color,
                }}>
                  {meta.label}
                </span>
                <span style={{ fontSize: 12, color: BN.ink4 }}>{meta.desc}</span>
                <span style={{
                  marginLeft: 'auto',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12,
                  color: BN.ink3,
                }}>
                  {bottleCount} btl / {allRows.length} SKU
                </span>
              </div>

              {/* Rows */}
              <div style={{
                background: BN.surf,
                border: `1px solid ${BN.hair}`,
                borderRadius: 8,
                overflow: 'hidden',
              }}>
                {visible.map((r, i) => (
                  <DrinkSoonRow key={r.key} row={r} index={i} statusColor={meta.color} />
                ))}
              </div>

              {/* Show more */}
              {!showAll[sg] && allRows.length > LIMIT && (
                <button
                  onClick={() => setShowAll(prev => ({ ...prev, [sg]: true }))}
                  style={{
                    marginTop: 10,
                    background: 'transparent',
                    border: `1px solid ${BN.hair2}`,
                    borderRadius: 6,
                    color: BN.ink3,
                    fontSize: 12,
                    padding: '6px 16px',
                    cursor: 'pointer',
                    letterSpacing: '0.05em',
                  }}
                >
                  Show {allRows.length - LIMIT} more
                </button>
              )}
            </section>
          );
        })}

        {totalBottles === 0 && (
          <p style={{ color: BN.ink4, fontSize: 14, textAlign: 'center', paddingTop: 48 }}>
            No bottles approaching their drinking window.
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row component
// ---------------------------------------------------------------------------
function DrinkSoonRow({
  row,
  index,
  statusColor,
}: {
  row: GroupedInventoryRow;
  index: number;
  statusColor: string;
}) {
  const isMobile = useIsMobile();
  const w = row.vintage.drinking_window_current;
  const windowStr = w ? `${w.from_year}–${w.to_year}` : '—';
  const peakStr = w && w.peak_from && w.peak_to ? `${w.peak_from}–${w.peak_to}` : null;
  const year = row.vintage.year === 1001 ? 'NV' : String(row.vintage.year);

  if (isMobile) {
    return (
      <Link
        to={`/wines/${row.wine.id}?vintage=${row.vintage.year}`}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '12px 14px', textDecoration: 'none', borderBottom: `1px solid ${BN.hair}` }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
          <span style={{ fontFamily: "'Fraunces', serif", fontStyle: 'italic', color: statusColor, fontSize: 13, fontWeight: 300, opacity: 0.7, flexShrink: 0 }}>
            {String(index + 1).padStart(2, '0')}.
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 15, fontWeight: 400, color: BN.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.producer.name}
            </div>
            <div style={{ fontSize: 11, color: BN.ink3, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.wine.cuvee_name}
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: BN.ink2 }}>{year} · ×{row.qty}</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: BN.ink3, marginTop: 2 }}>{windowStr}</div>
          {peakStr && <div style={{ fontSize: 11, color: statusColor, marginTop: 1, opacity: 0.85 }}>peak {peakStr}</div>}
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/wines/${row.wine.id}?vintage=${row.vintage.year}`}
      style={{ display: 'grid', gridTemplateColumns: '28px 1fr 56px 110px 80px 52px', gap: 12, padding: '13px 18px', textDecoration: 'none', alignItems: 'center', borderBottom: `1px solid ${BN.hair}`, transition: 'background 0.12s' }}
      onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(200,178,135,0.04)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
    >
      <span style={{ fontFamily: "'Fraunces', serif", fontStyle: 'italic', color: statusColor, fontSize: 14, fontWeight: 300, opacity: 0.7 }}>
        {String(index + 1).padStart(2, '0')}.
      </span>
      <div style={{ overflow: 'hidden' }}>
        <div style={{ fontFamily: "'Fraunces', serif", fontSize: 15, fontWeight: 400, color: BN.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.producer.name}
        </div>
        <div style={{ fontSize: 11, color: BN.ink3, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.wine.cuvee_name}{row.cellar?.name ? ` · ${row.cellar.name}` : ''}
        </div>
      </div>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: BN.ink2, textAlign: 'right' }}>{year}</span>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: BN.ink2 }}>{windowStr}</div>
        {peakStr && <div style={{ fontSize: 11, color: statusColor, marginTop: 2, opacity: 0.85 }}>peak {peakStr}</div>}
      </div>
      <span style={{ fontSize: 11, color: BN.ink4, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {row.cellar?.name ?? '—'}
      </span>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: BN.ink3, textAlign: 'right' }}>×{row.qty}</span>
    </Link>
  );
}
